import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ResizableImage } from "./extensions/ResizableImage";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useRef } from "react";
import {
  Bold, Italic, Underline as UIcon, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Code, Link2, ImagePlus, Sigma, Undo, Redo,
} from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";

const Btn = ({ onClick, active, children, title, testid }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onClick={onClick}
    className={`h-9 w-9 flex items-center justify-center rounded-md border transition-colors ${
      active
        ? "bg-nasa-blue text-white border-nasa-blue"
        : "bg-white text-ink-700 border-paper-200 hover:bg-paper-50 hover:text-nasa-blue"
    }`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder = "Escribe aquí…" }) {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false, HTMLAttributes: { rel: "noopener" } } }),
      ResizableImage.configure({ inline: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt("URL del enlace:");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  const insertMath = () => {
    editor.chain().focus().insertContent(" $x^2$ ").run();
    toast.info("Fórmula insertada. Escribe LaTeX entre los símbolos $ … $");
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      editor.chain().focus().setImage({ src: mediaUrl(data.url) }).updateAttributes("image", { width: "360px", align: "center" }).run();
      toast.success("Imagen subida");
    } catch (err) {
      toast.error("No se pudo subir la imagen");
    }
    e.target.value = "";
  };

  return (
    <div className="border border-paper-200 rounded-md bg-white overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap gap-1.5 p-2 border-b border-paper-200 bg-paper-50">
        <Btn testid="rte-bold" title="Negrita" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></Btn>
        <Btn testid="rte-italic" title="Cursiva" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></Btn>
        <Btn testid="rte-underline" title="Subrayado" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UIcon size={16} /></Btn>
        <Btn testid="rte-strike" title="Tachado" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={16} /></Btn>
        <div className="w-px bg-paper-200 mx-1" />
        <Btn testid="rte-h1" title="Título 1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 size={16} /></Btn>
        <Btn testid="rte-h2" title="Título 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={16} /></Btn>
        <Btn testid="rte-h3" title="Título 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={16} /></Btn>
        <div className="w-px bg-paper-200 mx-1" />
        <Btn testid="rte-ul" title="Lista" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={16} /></Btn>
        <Btn testid="rte-ol" title="Lista numerada" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={16} /></Btn>
        <Btn testid="rte-quote" title="Cita" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={16} /></Btn>
        <Btn testid="rte-code" title="Código" active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()}><Code size={16} /></Btn>
        <div className="w-px bg-paper-200 mx-1" />
        <Btn testid="rte-link" title="Enlace" active={editor.isActive("link")} onClick={addLink}><Link2 size={16} /></Btn>
        <Btn testid="rte-image" title="Imagen" onClick={() => fileRef.current?.click()}><ImagePlus size={16} /></Btn>
        <Btn testid="rte-math" title="Fórmula LaTeX" onClick={insertMath}><Sigma size={16} /></Btn>
        <div className="w-px bg-paper-200 mx-1" />
        <Btn testid="rte-undo" title="Deshacer" onClick={() => editor.chain().focus().undo().run()}><Undo size={16} /></Btn>
        <Btn testid="rte-redo" title="Rehacer" onClick={() => editor.chain().focus().redo().run()}><Redo size={16} /></Btn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImage} />
      </div>
      <EditorContent editor={editor} className="rich-content px-5 py-4 max-h-[520px] overflow-y-auto" />
    </div>
  );
}
